/**
 * localStorage that cannot take the page down.
 *
 * WHY: `window.localStorage` does not merely fail to store things when the
 * browser disallows it — **touching the property itself throws**. Safari with
 * "Block All Cookies" raises `SecurityError: The operation is insecure` on
 * access, before any get/set. Quota exhaustion and some privacy extensions
 * throw on write.
 *
 * That matters because the shop reads the access token on paths where a throw
 * is fatal rather than inconvenient:
 *   - `bootstrapAccessTokenFromUrl` runs inside PageClient's RENDER, so an
 *     uncaught throw takes the whole page to the error boundary — a blank shop
 *     instead of a signed-out one;
 *   - `services/local.ts` reads it in `prepareHeaders`, i.e. on every single
 *     RTK Query request.
 *
 * A customer who cannot store a token should end up signed out and told why
 * (see cookieCapability + AUTH_EVENTS.COOKIES_BLOCKED), never staring at a
 * broken page. Every helper here degrades instead of throwing.
 *
 * Scope note: these helpers are applied to the AUTH path only. The rest of the
 * app (checkout caches, promo keys, redux slices, marketing popup) still calls
 * `window.localStorage` directly in ~50 places. Those are worth converting too,
 * but they are a separate change with a different blast radius.
 */

export const readLocalStorage = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

/** @returns false when the value could not be persisted. */
export const writeLocalStorage = (key: string, value: string): boolean => {
  try {
    window.localStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

export const removeLocalStorage = (key: string): void => {
  try {
    window.localStorage.removeItem(key)
  } catch {
    // Nothing to do: if we cannot reach storage we also cannot have written it.
  }
}

const PROBE_KEY = '__ls_probe'

let usable: boolean | null = null

/**
 * Can this browser actually persist to localStorage? A full write/read/delete
 * cycle, because a read alone passes in browsers that accept reads and reject
 * writes. Memoised — one probe per page load.
 */
export const isLocalStorageUsable = (): boolean => {
  if (usable !== null) return usable
  if (typeof window === 'undefined') return false
  try {
    window.localStorage.setItem(PROBE_KEY, '1')
    usable = window.localStorage.getItem(PROBE_KEY) === '1'
    window.localStorage.removeItem(PROBE_KEY)
  } catch {
    usable = false
  }
  return usable
}
